// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { DndContext } from "@dnd-kit/core";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import equal from "fast-deep-equal";
import type { MouseEvent } from "react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import { getColumnCapabilities } from "@/lib/column-capabilities-constants";
import { createSingleColumnResizeHandler } from "@/lib/column-resize-utils";
import { formatDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";

// TYPES --------------------------------------------------------------
interface SortableTableProps<T> {
  columns: UnifiedTableColumn<T>[];
  data: T[] | null;
  isLoading?: boolean;
  loadingRowCount?: number;
  keyExtractor: (item: T) => string | number;
  defaultSort?: {
    key: keyof T | string;
    direction: SortDirection;
  };
  onSort?: (key: string | keyof T, direction: SortDirection) => void;
  // Storage key for persisting column state
  tableName: string;
  // Reset trigger - when this value changes, table resets to defaults
  resetTrigger?: number | string;
  // Disable internal scrolling for nested contexts (like drilldowns)
  disableInternalScrolling?: boolean;
  // Custom class name for table header
  headerClassName?: string;
}

// COMPONENT ----------------------------------------------------------
export function SortableTable<T extends object>({
  columns,
  data,
  isLoading = false,
  loadingRowCount = 5,
  keyExtractor,
  defaultSort,
  onSort,
  tableName,
  resetTrigger,
  disableInternalScrolling = false,
  headerClassName,
}: SortableTableProps<T>) {
  /* -----------------------------------------------------------------
   *  DERIVED VALUES & STATE
   * ----------------------------------------------------------------*/
  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.exportOnly),
    [columns],
  );

  // Convert SortableColumn to UnifiedTableColumn for compatibility
  const unifiedColumns = useMemo<UnifiedTableColumn<T>[]>(() => {
    return visibleColumns.map((col) => {
      const capabilities = getColumnCapabilities(col);
      return {
        key: col.key,
        title: col.title,
        sortable: capabilities.sortable,
        filterable: capabilities.filterable,
        filterType: col.filterType || "text",
        filterOptions: col.filterOptions || [],
        tooltip: col.tooltip,
        width: col.width,
        defaultSortDirection: col.defaultSortDirection || "asc",
        render: col.render,
      };
    });
  }, [visibleColumns]);

  // Column state management
  const {
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    sorting,
    setSorting,
    wrappedColumns,
    toggleColumnWrap,
    dndState: { sensors, handleDragEnd },
  } = usePersistedColumnState(tableName);

  // Initialize sorting from persisted state or default
  React.useEffect(() => {
    const initialSorting: SortingState = defaultSort
      ? [
          {
            id: defaultSort.key.toString(),
            desc: defaultSort.direction === "desc",
          },
        ]
      : [];

    if (sorting.length === 0 && initialSorting.length > 0) {
      setSorting(initialSorting);
    }
  }, [sorting.length, defaultSort, setSorting]);

  // Empty filters state for DraggableTableHeader compatibility
  const [filters] = useState<Record<string | keyof T, unknown>>(
    () => ({}) as Record<string | keyof T, unknown>,
  );

  // Helper to safely access nested values
  const getNestedValue = useCallback(
    (obj: T, key: keyof T | string): unknown => {
      if (typeof key !== "string") {
        return obj[key];
      }

      const keys = key.split(".");
      let value: unknown = obj;

      for (const k of keys) {
        if (value === null || value === undefined) return null;
        value = (value as Record<string, unknown>)[k];
      }

      return value;
    },
    [],
  );

  const renderCellValue = useCallback(
    (item: T, key: keyof T | string) => {
      const value = getNestedValue(item, key);

      // Dates → formatted
      if (
        value instanceof Date ||
        (typeof value === "string" && !Number.isNaN(Date.parse(value)))
      ) {
        const formattedDate =
          typeof value === "string"
            ? formatDate(value)
            : formatDate((value as Date).toISOString());
        return <span title={formattedDate}>{formattedDate}</span>;
      }

      const stringValue = String(value);
      return <span title={stringValue}>{stringValue}</span>;
    },
    [getNestedValue],
  );

  // Column definitions for TanStack with resizing enabled
  const columnDefs = useMemo<ColumnDef<T>[]>(
    () =>
      visibleColumns.map((col) => {
        const capabilities = getColumnCapabilities(col);
        return {
          id: col.key.toString(),
          header: col.title,
          accessorFn: (row) => getNestedValue(row as T, col.sortKey ?? col.key),
          enableSorting: capabilities.sortable,
          size: 150, // Default column width
          cell: ({ row }) => {
            // Use the original column's render function if available
            if (col.render) {
              return col.render(row.original);
            }
            return renderCellValue(row.original, col.key);
          },
          meta: {
            title: col.title,
            sortable: capabilities.sortable,
            filterable: capabilities.filterable,
            headStyle: "hover:bg-muted/80 hover:text-accent-foreground",
          },
        };
      }),
    [visibleColumns, getNestedValue, renderCellValue],
  );

  // React Table instance with column ordering and sizing
  const table = useReactTable({
    data: data ?? [],
    columns: columnDefs,
    state: {
      sorting,
      columnSizing,
      columnOrder,
    },
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {
      minSize: 80,
      maxSize: 500,
      size: 150,
    },
  });

  // Initialize column order when columnDefs change
  useEffect(() => {
    const currentIds = columnDefs.map((col) => col.id!);

    setColumnOrder((savedOrder) => {
      if (savedOrder.length === 0) {
        // No saved order, use default
        return currentIds;
      } else {
        // Filter out any saved columns that no longer exist and add any new columns at the end
        const validSavedColumns = savedOrder.filter((id) =>
          currentIds.includes(id),
        );
        const newColumns = currentIds.filter((id) => !savedOrder.includes(id));
        const newOrder = [...validSavedColumns, ...newColumns];

        if (!equal(newOrder, savedOrder)) {
          return newOrder;
        }
        return savedOrder;
      }
    });
  }, [columnDefs, setColumnOrder]);

  // Reset table when resetTrigger changes
  useEffect(() => {
    if (resetTrigger === undefined) return;

    // Reset sorting to default
    const initialSorting: SortingState = defaultSort
      ? [
          {
            id: defaultSort.key.toString(),
            desc: defaultSort.direction === "desc",
          },
        ]
      : [];
    setSorting(initialSorting);

    // Column state clearing is now handled by the organization-scoped storage hooks

    // Reset column state and reinitialize column order to maintain drag-and-drop
    setColumnSizing({});
    // Don't reset columnOrder to empty array - instead reinitialize with current column IDs
    setColumnOrder(() => columnDefs.map((col) => col.id!));
  }, [
    resetTrigger,
    defaultSort,
    columnDefs,
    setColumnOrder,
    setSorting,
    setColumnSizing,
  ]);

  // For row virtualization ------------------------------------------------
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const hasWrappedColumns = wrappedColumns.length > 0;
  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: hasWrappedColumns ? () => 72 : () => 48, // larger estimate when wrapping
    overscan: 10,
    measureElement: hasWrappedColumns
      ? (el) => el?.getBoundingClientRect().height ?? 72
      : undefined,
  });

  /* -----------------------------------------------------------------
   *  EVENT HANDLERS
   * ----------------------------------------------------------------*/
  const handleSort = (colId: string) => {
    // Find the original SortableColumn definition for default sort direction
    const sortableColumn = visibleColumns.find(
      (col) => col.key.toString() === colId,
    );
    const sortable = sortableColumn?.sortable;

    if (!sortable) return;

    let direction: SortDirection =
      sortableColumn?.defaultSortDirection || "asc";

    // Check current sort config
    const currentSort = sorting.find((s) => s.id === colId);
    if (currentSort) {
      // Toggle between asc and desc
      direction = currentSort.desc ? "asc" : "desc";
    }

    // Update TanStack sorting state
    const nextSorting: SortingState = [
      {
        id: colId,
        desc: direction === "desc",
      },
    ];
    setSorting(nextSorting);
    table.setSorting(nextSorting);

    if (onSort) {
      onSort(colId, direction);
    }
  };

  // Handle double-click on column resize handle to auto-size
  const handleDoubleClickResize = useCallback(
    (_: MouseEvent, columnId: string) => {
      // Minimum width for any column
      const MIN_WIDTH = 100;
      // Set a reasonable maximum width
      const MAX_WIDTH = 400;

      // Find all content in this column and determine the max width needed
      const columnCells = document.querySelectorAll(
        `[data-column-id="${columnId}"]`,
      );

      // Default to minimum width
      let maxWidth = MIN_WIDTH;

      // Measure each cell's content width
      columnCells.forEach((cell) => {
        const contentEl = cell.querySelector(".cell-content");
        if (contentEl) {
          const temp = document.createElement("span");
          try {
            // Create a temporary element to measure the full text width
            temp.style.position = "absolute";
            temp.style.visibility = "hidden";
            temp.style.whiteSpace = "nowrap";
            temp.innerText = contentEl.textContent || "";
            document.body.appendChild(temp);

            // Get the width
            const width = temp.offsetWidth + 20; // Add padding
            if (width > maxWidth && width < MAX_WIDTH) {
              maxWidth = width;
            }
          } finally {
            // Always remove temp element
            if (temp.parentNode) {
              document.body.removeChild(temp);
            }
          }
        }
      });

      // Update column sizing state
      setColumnSizing((prev) => ({
        ...prev,
        [columnId]: maxWidth,
      }));
    },
    [setColumnSizing],
  );

  // Create resize-to-fit handler using the utility function
  const handleResizeToFit = useMemo(
    () => createSingleColumnResizeHandler(setColumnSizing, "sortable-table"),
    [setColumnSizing],
  );

  // Map TanStack sorting → local sortConfig (for SortIcon UI)
  const sortConfig = useMemo(() => {
    if (!sorting.length) return { key: "", direction: null } as const;
    const { id, desc } = sorting[0];
    return {
      key: id,
      direction: desc ? ("desc" as const) : ("asc" as const),
    };
  }, [sorting]);

  /* -----------------------------------------------------------------
   *  RENDER HELPERS
   * ----------------------------------------------------------------*/
  const renderSortIcon = (column: UnifiedTableColumn<T>) => {
    if (!column.sortable) return null;
    return (
      <SortIcon
        isActive={sortConfig.key === column.key}
        direction={sortConfig.key === column.key ? sortConfig.direction : null}
      />
    );
  };

  // Placeholder functions for DraggableTableHeader compatibility
  const updateFilter = useCallback(() => {
    // No-op for now, can be extended in future
  }, []);

  const clearFilter = useCallback(() => {
    // No-op for now, can be extended in future
  }, []);

  const renderLoadingBody = () => (
    <TableBody className="bg-card">
      {Array.from({ length: loadingRowCount }).map((_, i) => (
        <TableRow key={i} className="border-b">
          {visibleColumns.map((column) => (
            <TableCell key={`${i}-${column.key.toString()}`}>
              <Skeleton className="h-4 w-[80%] bg-muted/60" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );

  const rows = table.getRowModel().rows;

  const renderVirtualizedBody = () => {
    if (!data || rows.length === 0) {
      return (
        <TableBody className="bg-card">
          <TableRow>
            <TableCell
              colSpan={visibleColumns.length}
              className="h-24 text-center"
            >
              No data available
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    const totalHeight = rowVirtualizer.getTotalSize();
    const virtualItems = rowVirtualizer.getVirtualItems();

    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const paddingBottom =
      virtualItems.length > 0
        ? totalHeight - virtualItems[virtualItems.length - 1].end
        : 0;

    return (
      <TableBody className="bg-card">
        {paddingTop > 0 && (
          <TableRow style={{ height: `${paddingTop}px` }}>
            <TableCell colSpan={visibleColumns.length} />
          </TableRow>
        )}

        {virtualItems.map((virtualRow: VirtualItem) => {
          const row = rows[virtualRow.index];
          const item = row.original as T;

          return (
            <TableRow
              ref={rowVirtualizer.measureElement}
              data-index={virtualRow.index}
              key={`${keyExtractor(item)}-${virtualRow.index}`}
              style={{ height: `${virtualRow.size}px` }}
            >
              {row.getVisibleCells().map((cell) => {
                const isWrapped = wrappedColumns.includes(cell.column.id);
                return (
                  <TableCell
                    key={cell.id}
                    style={{
                      width: cell.column.getSize(),
                      minWidth: cell.column.getSize(),
                      maxWidth: cell.column.getSize(),
                    }}
                    data-column-id={cell.column.id}
                  >
                    <div className={cn("cell-content", isWrapped && "wrapped")}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}

        {paddingBottom > 0 && (
          <TableRow style={{ height: `${paddingBottom}px` }}>
            <TableCell colSpan={visibleColumns.length} />
          </TableRow>
        )}
      </TableBody>
    );
  };

  const renderTableContent = () => {
    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={unifiedColumns}
          handleDoubleClickResize={handleDoubleClickResize}
          onResizeToFit={handleResizeToFit}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          headerClassName={headerClassName}
        />
        {isLoading ? renderLoadingBody() : renderVirtualizedBody()}
      </>
    );
  };

  /* -----------------------------------------------------------------
   *  ACTUAL RENDER
   * ----------------------------------------------------------------*/
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={constrainedClosestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToTableBounds]}
    >
      <div
        ref={tableContainerRef}
        className={cn(
          "relative w-full",
          !disableInternalScrolling && "overflow-auto",
          table.getState().columnSizingInfo.isResizingColumn &&
            "table-resizing",
        )}
        style={
          disableInternalScrolling ? {} : { maxHeight: "600px" } // limit height for scrolling / virtualization only when not in nested context
        }
      >
        <Table
          className="min-w-full table-fixed"
          style={{ width: table.getTotalSize() }}
          data-table-id="sortable-table"
          data-table-container
        >
          {renderTableContent()}
        </Table>
      </div>
    </DndContext>
  );
}
